import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { api, type AdminResponse, type ApiError } from "../lib/api";
import { durationShort, isoZ } from "../lib/format";
import {
  buildJsonExport,
  buildMarkdownExport,
  downloadBlob,
  exportFilenames,
} from "../lib/exporters";
import { ResponseCard } from "../components/admin/ResponseCard";
import { ResponseDetail } from "../components/admin/ResponseDetail";
import { CompareView } from "../components/admin/CompareView";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; status: number | null }
  | { kind: "ready"; responses: AdminResponse[] };

export function Admin() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selected, setSelected] = useState<AdminResponse | null>(null);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .adminListResponses()
      .then((data) => {
        if (cancelled) return;
        setState({ kind: "ready", responses: data.responses });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status =
          err && typeof err === "object" && "status" in err
            ? (err as ApiError).status
            : null;
        setState({ kind: "error", status });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-fg-muted">
        Lade Antworten …
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-sm text-fg-muted">
          Konnte Daten nicht laden{state.status ? ` (HTTP ${state.status})` : ""}. Bitte
          Browser-Login mit Admin-Credentials prüfen.
        </p>
      </div>
    );
  }

  return (
    <Loaded
      responses={state.responses}
      selected={selected}
      setSelected={setSelected}
      compare={compare}
      setCompare={setCompare}
    />
  );
}

function Loaded({
  responses,
  selected,
  setSelected,
  compare,
  setCompare,
}: {
  responses: AdminResponse[];
  selected: AdminResponse | null;
  setSelected: (r: AdminResponse | null) => void;
  compare: boolean;
  setCompare: (b: boolean) => void;
}) {
  const summary = useMemo(() => {
    if (responses.length === 0) {
      return { count: 0, last: null as string | null, avgMs: 0 };
    }
    const last = responses
      .map((r) => r.submittedAt)
      .sort()
      .at(-1) ?? null;
    const avgMs = Math.round(
      responses.reduce((s, r) => s + r.durationMs, 0) / responses.length,
    );
    return { count: responses.length, last, avgMs };
  }, [responses]);

  function exportJson() {
    const { json } = exportFilenames();
    downloadBlob(json, "application/json", buildJsonExport(responses));
  }
  function exportMarkdown() {
    const { md } = exportFilenames();
    downloadBlob(md, "text/markdown", buildMarkdownExport(responses));
  }

  const canCompare = responses.length >= 2;
  const compareA = canCompare ? responses[0] : null;
  const compareB = canCompare ? responses[1] : null;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
          <p className="text-sm text-fg-muted">
            {summary.count} Antwort{summary.count === 1 ? "" : "en"}
            {summary.last ? ` · zuletzt ${isoZ(summary.last)}` : ""}
            {summary.count > 0 ? ` · ⌀ ${durationShort(summary.avgMs)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={responses.length === 0}
            onClick={exportJson}
            className="rounded-input border border-border px-3 py-2 text-xs transition hover:border-accent disabled:cursor-not-allowed disabled:opacity-30"
          >
            Als JSON
          </button>
          <button
            type="button"
            disabled={responses.length === 0}
            onClick={exportMarkdown}
            className="rounded-input bg-accent px-3 py-2 text-xs font-medium text-accent-fg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Als Markdown
          </button>
        </div>
      </header>

      {canCompare ? (
        <div className="flex items-center gap-2 rounded-input border border-border bg-bg-elevated p-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            Compare-View (
            {compareA?.participant} vs. {compareB?.participant})
          </label>
        </div>
      ) : null}

      {compare && compareA && compareB ? (
        <section className="rounded-card border border-border bg-bg-elevated p-5">
          <CompareView a={compareA} b={compareB} />
        </section>
      ) : responses.length === 0 ? (
        <div className="rounded-card border border-dashed border-border p-8 text-center text-sm text-fg-muted">
          Noch keine Antworten.
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {responses.map((r) => (
            <ResponseCard
              key={r.id}
              response={r}
              onClick={() => setSelected(r)}
            />
          ))}
        </section>
      )}

      <Dialog.Root
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(94vw,42rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-card border border-border bg-bg-elevated shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3">
              <Dialog.Title className="font-mono text-sm">
                {selected?.participant}
              </Dialog.Title>
              <Dialog.Close className="text-fg-muted text-sm hover:text-fg">
                Schliessen
              </Dialog.Close>
            </div>
            <ScrollArea.Root className="h-[80vh]">
              <ScrollArea.Viewport className="h-full px-5 py-5">
                {selected ? <ResponseDetail response={selected} /> : null}
              </ScrollArea.Viewport>
              <ScrollArea.Scrollbar
                orientation="vertical"
                className="flex w-1.5 touch-none select-none bg-transparent p-px"
              >
                <ScrollArea.Thumb className="flex-1 rounded-full bg-border" />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
